import React, { useState, useEffect } from "react";
import { useVenue } from "../context/VenueContext";
import { useAuth } from "../context/AuthContext";
import { searchSpotify } from "../services/spotifyService";
import { addSongToQueue } from "../services/queueService";
import { recordSongRequest } from "../services/userService";

export default function SearchScreen({ setScreen, onNotif }) {
  const { venueId, queue, nowPlaying } = useVenue();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [added, setAdded] = useState([]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const delay = setTimeout(async () => {
      const r = await searchSpotify(query);
      setResults(r);
    }, 400);
    return () => clearTimeout(delay);
  }, [query]);

  const handleAdd = async (song) => {
    if (queue.find(s => s.id === song.id) || nowPlaying?.id === song.id || added.includes(song.id)) {
      onNotif("Already in queue", "error"); return;
    }
    await addSongToQueue(venueId, song, user);
    if (user && !user.isGuest) await recordSongRequest(user.uid, song);
    setAdded(prev => [...prev, song.id]);
    onNotif(`"${song.title}" added ✓`);
    setTimeout(() => setScreen("queue"), 400);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A0A", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display:ital@0;1&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 0; }
        .search-header { padding: 48px 40px 24px; position: relative; max-width: 800px; margin: 0 auto; text-align: center; }
        .search-header-glow { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 0%, rgba(29,185,84,0.08) 0%, transparent 60%); pointer-events: none; }
        .search-back { background: none; border: none; font-size: 20px; cursor: pointer; color: rgba(255,255,255,0.5); padding: 0; font-family: 'DM Sans', sans-serif; transition: color 0.2s; }
        .search-back:hover { color: rgba(255,255,255,0.8); }
        .search-title { font-family: 'DM Serif Display', serif; font-size: 22px; color: #ffffff; letter-spacing: -0.5px; }
        .search-input { width: 100%; padding: 14px 44px 14px 16px; border-radius: 12px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); font-family: 'DM Sans', sans-serif; font-size: 15px; color: #ffffff; outline: none; transition: all 0.2s; }
        .search-input:focus { border-color: rgba(255,255,255,0.3); }
        .search-input::placeholder { color: rgba(255,255,255,0.3); }
        .platform-pills { display: flex; gap: 8px; padding: 12px 40px; overflow-x: auto; max-width: 800px; margin: 0 auto; justify-content: center; }
        .pill { padding: 6px 14px; border-radius: 100px; font-size: 12px; font-weight: 600; white-space: nowrap; cursor: pointer; flex-shrink: 0; font-family: 'DM Sans', sans-serif; transition: all 0.2s; }
        .pill-active { background: rgba(29,185,84,0.15); color: #1DB954; border: 1px solid rgba(29,185,84,0.3); }
        .pill-inactive { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.5); border: 1px solid rgba(255,255,255,0.08); }
        .pill-inactive:hover { background: rgba(255,255,255,0.06); }
        .result-item { display: flex; align-items: center; gap: 12px; padding: 14px 24px; cursor: pointer; transition: background 0.2s; border-bottom: 1px solid rgba(255,255,255,0.06); max-width: 800px; margin: 0 auto; }
        .result-item:active { background: rgba(255,255,255,0.04); }
        .result-cover { width: 46px; height: 46px; border-radius: 10px; object-fit: cover; flex-shrink: 0; }
        .result-add-btn { padding: 8px 16px; border-radius: 100px; border: none; font-size: 13px; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; font-family: 'DM Sans', sans-serif; }
        .result-add-btn:active { transform: scale(0.93); }
        .result-add-btn-default { background: #1DB954; color: #ffffff; }
        .result-add-btn-added { background: rgba(29,185,84,0.15); color: #1DB954; border: 1px solid rgba(29,185,84,0.3); }
      `}</style>

      <div className="search-header">
        <div className="search-header-glow" />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, position: "relative", zIndex: 1 }}>
          <button className="search-back" onClick={() => setScreen("home")}>‹</button>
          <div className="search-title">Request a Song</div>
        </div>
        <div style={{ position: "relative", zIndex: 1 }}>
          <input className="search-input" placeholder="Song, artist, or vibe..." value={query} onChange={e => setQuery(e.target.value)} autoFocus />
          <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.3)", fontSize: 16 }}>⌕</span>
        </div>
      </div>

      <div className="platform-pills">
        {["Spotify", "YouTube", "Apple Music", "SoundCloud"].map((p, i) => (
          <div key={p} className={`pill ${i === 0 ? "pill-active" : "pill-inactive"}`}>{p}</div>
        ))}
      </div>

      {results.length > 0 ? (
        results.map(song => {
          const isAdded = queue.find(s => s.id === song.id) || nowPlaying?.id === song.id || added.includes(song.id);
          return (
            <div key={song.id} className="result-item" onClick={() => !isAdded && handleAdd(song)}>
              <img className="result-cover" src={song.cover} alt="" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", letterSpacing: -0.3 }}>{song.title}</div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>{song.artist} · {song.duration}</div>
              </div>
              <button className={`result-add-btn ${isAdded ? "result-add-btn-added" : "result-add-btn-default"}`}>
                {isAdded ? "✓ Added" : "+ Add"}
              </button>
            </div>
          );
        })
      ) : (
        <div style={{ padding: "60px 20px", textAlign: "center", fontSize: 14, lineHeight: 1.6 }}>
          {query.length > 1 ? (
            <span style={{ color: "rgba(255,255,255,0.4)" }}>No songs found</span>
          ) : (
            <>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎵</div>
              <div style={{ fontWeight: 600, color: "#ffffff", marginBottom: 4 }}>Search for any song</div>
              <div style={{ color: "rgba(255,255,255,0.4)" }}>Try "Blinding Lights" or "Drake"</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
